/**
 * ctaMarker — Cross-agent handoff CTA marker stripping (render-time).
 *
 * The Experto agent appends the HTML marker `<!-- CTA:create-plan -->` to its
 * message to signal a "plan de trabajo" handoff (see
 * docs/architecture/agents/CROSS_AGENT_HANDOFF.md). N8N's "Inject Plan CTA" node strips
 * it from the FINAL persisted message before it ever reaches the backend — but
 * the live-streaming path delivers the raw LLM tokens straight to the client
 * and never passes through that node. Since react-markdown renders raw HTML
 * comments as literal text (no rehype-raw), the marker leaks verbatim into the
 * streaming bubble until the persisted message swaps in.
 *
 * These helpers replicate the N8N strip on the render side:
 *   - stripCtaMarker           complete marker only (persisted bubble; defense-in-depth)
 *   - stripCtaMarkerStreaming  complete marker + a trailing partial being typed
 *
 * The marker string is the single source of truth; COMPLETE_MARKER mirrors the
 * whitespace-tolerant regex in the N8N node.
 */

/** Canonical marker emitted by the agent prompt. */
export const CTA_CREATE_PLAN_MARKER = '<!-- CTA:create-plan -->';

/** Complete marker, whitespace-tolerant, global — mirrors the N8N regex. */
const COMPLETE_MARKER = /<!--\s*CTA:create-plan\s*-->/g;

/**
 * Remove every complete CTA marker from `text`. Safe for finished messages.
 * @param {string} text
 * @returns {string} the same value when there is nothing to strip
 */
export function stripCtaMarker(text) {
  if (!text) return text;
  return text.replace(COMPLETE_MARKER, '');
}

/**
 * Length of the trailing substring of `text` that is a strict prefix of the
 * marker mid-typing (e.g. `<!-- CTA:cre`). Returns 0 when none.
 *
 * Only prefixes that already opened an HTML comment (`<!--`, length ≥ 4) count,
 * so a stray trailing `<`, `<!` or `<!-` in prose or code is never touched.
 * @param {string} text
 * @returns {number}
 */
function trailingMarkerPartialLength(text) {
  const marker = CTA_CREATE_PLAN_MARKER;
  const max = Math.min(text.length, marker.length - 1);
  for (let len = max; len >= 4; len--) {
    if (text.endsWith(marker.slice(0, len))) return len;
  }
  return 0;
}

/**
 * Streaming variant: strips complete markers AND a trailing partial marker
 * still being streamed token by token, so the live bubble never flashes a
 * half-typed `<!-- CTA:cre…` before the closing `-->` arrives.
 * @param {string} text
 * @returns {string} the same value when there is nothing to strip
 */
export function stripCtaMarkerStreaming(text) {
  if (!text) return text;
  const out = stripCtaMarker(text);
  const partial = trailingMarkerPartialLength(out);
  return partial > 0 ? out.slice(0, out.length - partial) : out;
}
